import { css, cva } from "styled-system/css";
import { contentMaxWidth } from "#/capacitor-app.css";

// Page container
export const containerClass = css({
  minHeight: "100vh",
  backgroundColor: "bgBase100",
  paddingBottom: "sm",
});

// Header container
export const headerContainerClass = css({
  backgroundColor: "bgBase100",
  borderBottomWidth: "1px",
  borderBottomStyle: "solid",
  borderBottomColor: "bgBase300",
  marginBottom: "md",
  paddingBlock: "sm",
  position: "sticky",
  top: 0,
  zIndex: 20,
  boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  width: "100%",
});

export const headerInnerClass = css({
  width: "100%",
  maxWidth: contentMaxWidth,
  marginInline: "auto",
  paddingInline: "lg",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// Nav sections
export const navSectionSideStyles = css.raw({
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "sm",
  display: "flex",
  paddingX: 0,
  paddingY: 0,
});

export const navSectionCenterClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
  flex: 1,
  paddingInline: "sm",
});

// Icon button
export const iconButtonStyles = css.raw({
  cursor: "pointer",
  transitionProperty: "all",
  transitionDuration: "200ms",
  userSelect: "none",
  border: "none",
  outline: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2.5rem",
  height: "2.5rem",
  borderRadius: "full",
  backgroundColor: "transparent",
  color: "inherit",
  _hover: {
    backgroundColor: "bgBase200",
  },
  _disabled: {
    opacity: 0.3,
    cursor: "not-allowed",
  },
});

// Title button
export const titleButton = cva({
  base: {
    cursor: "pointer",
    transitionProperty: "all",
    transitionDuration: "200ms",
    userSelect: "none",
    borderWidth: "1px",
    borderStyle: "solid",
    outline: "none",
    paddingInline: "md",
    paddingY: "xs",
    fontSize: { base: "sm", sm: "base" },
    fontWeight: "700",
    borderRadius: "sm",
    maxWidth: "100%",
    display: "flex",
    justifyContent: "flex-start",
    overflow: "hidden",
    whiteSpace: "nowrap",
    "& span": {
      display: "inline-block",
      minWidth: "100%",
    },
  },
  variants: {
    active: {
      true: {
        color: "primaryContent",
        backgroundColor: "primary",
        borderColor: "primary",
        boxShadow: "sm",
        _hover: {
          backgroundColor: "primary",
          opacity: 0.9,
        },
      },
      false: {
        backgroundColor: "transparent",
        color: "textBase",
        borderColor: "bgBase300",
        _hover: {
          backgroundColor: "bgBase200",
        },
      },
    },
  },
  defaultVariants: {
    active: false,
  },
});

export const marqueeStyles = css.raw({
  animation: "marquee 15s linear infinite",
});

// Dropdown menu
export const dropdownMenuClass = css({
  position: "absolute",
  top: "100%",
  left: "50%",
  transform: "translateX(-50%)",
  marginTop: "0.25rem",
  zIndex: 50,
  backgroundColor: "bgBase100",
  borderRadius: "medium",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "textBase/10",
  boxShadow: "xl",
  padding: "sm",
  width: "14rem",
  listStyle: "none",
  margin: 0,
  display: "flex",
  flexDirection: "column",
});

// Menu item styles (shared)
export const menuItemStyles = css.raw({
  cursor: "pointer",
  transitionProperty: "all",
  transitionDuration: "200ms",
  userSelect: "none",
  border: "none",
  outline: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "sm",
  width: "100%",
  paddingInline: "sm",
  paddingY: "xs",
  borderRadius: "small",
  fontSize: "sm",
  fontWeight: "500",
  backgroundColor: "transparent",
  color: "textBase",
  textAlign: "left",
  textDecoration: "none",
  _hover: {
    backgroundColor: "bgBase200",
  },
});

// Menu item (for anchor tags)
export const menuItemClass = css(menuItemStyles);

// Menu item li
export const menuItemLiClass = css({
  margin: 0,
  padding: 0,
});

// Content area
export const contentClass = css({
  maxWidth: contentMaxWidth,
  marginInline: "auto",
  paddingInline: "lg",
  fontSize: "1.125rem",
  lineHeight: 1.75,
  color: "textBase/80",
});

export const contentInnerClass = css({
  // maxWidth: "48rem", // Keep readable width for the actual text
  // marginInline: "auto",
});

// Paragraph
export const paragraphClass = css({
  marginBottom: "md",
});

// Sentence
export const sentence = cva({
  base: {
    borderRadius: "2px",
  },
  variants: {
    active: {
      true: {
        backgroundColor: "rgba(254, 240, 138, 0.5)",
        color: "#000000",
        fontWeight: "500",
      },
    },
  },
});

// Dialog overlay
export const dialogOverlayClass = css({
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "md",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  backdropFilter: "blur(4px)",
});

// Dialog box
export const dialogBoxClass = css({
  width: "100%",
  maxWidth: "24rem",
  overflow: "hidden",
  backgroundColor: "bgBase100",
  borderRadius: "large",
  padding: "xl",
  boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
});

// Dialog header
export const dialogHeaderClass = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "md",
});

// Dialog title
export const dialogTitleClass = css({
  fontSize: "h3",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "-0.025em",
});

// Dialog description
export const dialogDescriptionClass = css({
  marginBottom: "md",
  fontSize: "sm",
  color: "textBase/70",
});

// Dialog body
export const dialogBodyClass = css({
  display: "flex",
  flexDirection: "column",
  gap: "sm",
  marginBottom: "xl",
});

// Dialog label
export const dialogLabelClass = css({
  fontSize: "xs",
  fontWeight: "900",
  textTransform: "uppercase",
  color: "textBase/50",
});

// Dialog actions
export const dialogActionsClass = css({
  display: "flex",
  justifyContent: "flex-end",
  gap: "md",
});

export const headerClass = css({
  marginBottom: "md",
  fontSize: "1.25rem",
  fontWeight: "bold",
  marginTop: "1em",
  color: "textBase",
});
